import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ShieldAlert, RefreshCw, Search, Filter, AlertTriangle, FileWarning, Activity, Calendar, Download, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANCHES = ["QC", "PQ", "CV", "PM", "VZ", "OTG-A", "OTG-B", "OTG-C"];

const ACTION_TYPE_COLORS = {
  RECEIVE: "bg-green-100 text-green-800 border-green-200",
  PULL_OUT: "bg-red-100 text-red-800 border-red-200",
  RECOUNT: "bg-blue-100 text-blue-800 border-blue-200",
  WALK_IN_SALE: "bg-orange-100 text-orange-800 border-orange-200",
  ONLINE_SALE: "bg-orange-100 text-orange-800 border-orange-200",
  TRANSFER_OUT: "bg-purple-100 text-purple-800 border-purple-200",
  TRANSFER_IN: "bg-purple-100 text-purple-800 border-purple-200",
  WARRANTY_WARRANTY_CLAIM: "bg-amber-100 text-amber-800 border-amber-200",
  WARRANTY_REPLACEMENT: "bg-amber-100 text-amber-800 border-amber-200",
  WARRANTY_DEFECTIVE: "bg-amber-100 text-amber-800 border-amber-200",
  WARRANTY_RETURN_TO_STOCK: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

function formatDateTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-PH", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function StockAudit() {
  const { user, isAdmin } = useAuth();
  const [tab, setTab] = useState("movements"); // 'movements' | 'tally'

  // Movement Logs state
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchFilter, setBranchFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [itemSearch, setItemSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [suspiciousOnly, setSuspiciousOnly] = useState(false);

  // Tally Report state
  const [tally, setTally] = useState([]);
  const [tallyLoading, setTallyLoading] = useState(false);
  const [tallyBranch, setTallyBranch] = useState("QC");
  const [tallyDays, setTallyDays] = useState(30);

  // Discrepancy Alerts state
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertStatusFilter, setAlertStatusFilter] = useState("OPEN");
  const [resolvingAlert, setResolvingAlert] = useState(null);
  const [resolutionText, setResolutionText] = useState("");

  // Snapshot Watch state
  const [snapReport, setSnapReport] = useState(null);
  const [snapLoading, setSnapLoading] = useState(false);
  const [snapBranch, setSnapBranch] = useState("QC");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (branchFilter !== "all") params.branch = branchFilter;
      if (actionFilter !== "all") params.action_type = actionFilter;
      if (itemSearch.trim()) params.item_name = itemSearch.trim();
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const res = await axios.get(`${API}/movement-logs`, { params });
      setLogs(res.data.logs || []);
    } catch (e) {
      toast.error("Failed to load movement logs");
    } finally {
      setLoading(false);
    }
  }, [branchFilter, actionFilter, itemSearch, startDate, endDate]);

  const fetchTally = useCallback(async () => {
    setTallyLoading(true);
    try {
      const res = await axios.get(`${API}/stock-tally`, {
        params: { branch: tallyBranch, days: tallyDays },
      });
      setTally(res.data.items || []);
    } catch (e) {
      toast.error("Failed to load tally report");
    } finally {
      setTallyLoading(false);
    }
  }, [tallyBranch, tallyDays]);

  useEffect(() => {
    if (tab === "movements") fetchLogs();
  }, [tab, fetchLogs]);

  useEffect(() => {
    if (tab === "tally") fetchTally();
  }, [tab, fetchTally]);

  const fetchAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const params = {};
      if (alertStatusFilter !== "all") params.status = alertStatusFilter;
      const res = await axios.get(`${API}/stock-discrepancies`, { params });
      setAlerts(res.data.discrepancies || []);
    } catch (e) {
      toast.error("Failed to load discrepancy alerts");
    } finally {
      setAlertsLoading(false);
    }
  }, [alertStatusFilter]);

  useEffect(() => {
    if (tab === "alerts") fetchAlerts();
  }, [tab, fetchAlerts]);

  const fetchSnapReport = useCallback(async () => {
    setSnapLoading(true);
    try {
      const res = await axios.get(`${API}/stock-snapshots/discrepancy`, { params: { branch: snapBranch } });
      setSnapReport(res.data);
      if (res.data.message) toast.info(res.data.message);
    } catch (e) {
      toast.error("Failed to load snapshot report");
    } finally {
      setSnapLoading(false);
    }
  }, [snapBranch]);

  useEffect(() => {
    if (tab === "snapshot") fetchSnapReport();
  }, [tab, fetchSnapReport]);

  const takeSnapshotNow = async () => {
    try {
      const res = await axios.post(`${API}/stock-snapshots/take`);
      toast.success(res.data.message);
      fetchSnapReport();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to take snapshot");
    }
  };

  const resolveAlert = async () => {
    if (!resolvingAlert) return;
    if (resolutionText.trim().length < 5) {
      toast.error("Kailangan ng explanation (min 5 characters)");
      return;
    }
    try {
      await axios.put(`${API}/stock-discrepancies/${resolvingAlert.id}/resolve`, { resolution: resolutionText });
      toast.success("✅ Discrepancy resolved");
      setResolvingAlert(null);
      setResolutionText("");
      fetchAlerts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resolve");
    }
  };

  // Mark suspicious recounts (large changes, negative adjustments without reason)
  const isSuspicious = (log) => {
    if (log.action_type === "RECOUNT") {
      const qty = Math.abs(Number(log.quantity || 0));
      // Large recount (>10 units) or missing reason
      if (qty >= 10) return true;
      if (!log.notes || log.notes.length < 10) return true;
    }
    return false;
  };

  const filteredLogs = suspiciousOnly ? logs.filter(isSuspicious) : logs;

  const exportLogsCSV = () => {
    if (filteredLogs.length === 0) {
      toast.error("No logs to export");
      return;
    }
    const headers = ["Timestamp", "Action", "Branch", "Item", "Quantity", "Previous", "New", "Location", "Encoder", "Notes"];
    const rows = filteredLogs.map((l) => [
      l.timestamp || "",
      l.action_type || "",
      l.branch || l.from_branch || "",
      l.item_name || "",
      l.quantity ?? "",
      l.previous_stock ?? "",
      l.new_stock ?? "",
      l.location || "",
      l.encoder || "",
      (l.notes || "").replace(/"/g, '""'),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${c}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stock-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runNormalize = async () => {
    if (!isAdmin()) {
      toast.error("Admin only");
      return;
    }
    if (!window.confirm("Normalize all warranty location names? (WARRANTY RESERVE → Warranty Reserve, etc.) This is safe and reversible.")) return;
    try {
      const res = await axios.post(`${API}/warranty-locations/normalize`);
      toast.success(`✅ Normalized ${res.data.total_normalized} location record(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Normalize failed");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4" data-testid="stock-audit-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-red-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Stock Audit & Anti-Theft</h1>
              <p className="text-xs text-gray-500">Audit trail ng lahat ng stock movements. Mag-detect ng discrepancies at suspicious activity.</p>
            </div>
          </div>
          {isAdmin() && (
            <Button
              variant="outline"
              size="sm"
              onClick={runNormalize}
              data-testid="normalize-warranty-btn"
              className="text-xs"
              title="Standardize warranty location names (admin only)"
            >
              <Lock className="h-3.5 w-3.5 mr-1" />
              Normalize Warranty Locations
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-gray-200">
          <button
            onClick={() => setTab("movements")}
            data-testid="tab-movements"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === "movements" ? "border-red-600 text-red-700" : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <Activity className="h-4 w-4 inline mr-1" />
            Movement Audit Log
          </button>
          <button
            onClick={() => setTab("tally")}
            data-testid="tab-tally"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === "tally" ? "border-red-600 text-red-700" : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <FileWarning className="h-4 w-4 inline mr-1" />
            Daily Tally Report
          </button>
          <button
            onClick={() => setTab("alerts")}
            data-testid="tab-alerts"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === "alerts" ? "border-red-600 text-red-700" : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <AlertTriangle className="h-4 w-4 inline mr-1" />
            Discrepancy Alerts
          </button>
          <button
            onClick={() => setTab("snapshot")}
            data-testid="tab-snapshot"
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === "snapshot" ? "border-red-600 text-red-700" : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            <Calendar className="h-4 w-4 inline mr-1" />
            Snapshot Watch
          </button>
        </div>

        {tab === "movements" && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
                <Select value={branchFilter} onValueChange={setBranchFilter}>
                  <SelectTrigger data-testid="branch-filter"><SelectValue placeholder="Branch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {BRANCHES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={actionFilter} onValueChange={setActionFilter}>
                  <SelectTrigger data-testid="action-filter"><SelectValue placeholder="Action" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    <SelectItem value="RECEIVE">Receive</SelectItem>
                    <SelectItem value="PULL_OUT">Pull Out</SelectItem>
                    <SelectItem value="RECOUNT">Recount</SelectItem>
                    <SelectItem value="WALK_IN_SALE">Walk-In Sale</SelectItem>
                    <SelectItem value="ONLINE_SALE">Online Sale</SelectItem>
                    <SelectItem value="TRANSFER_OUT">Transfer Out</SelectItem>
                    <SelectItem value="TRANSFER_IN">Transfer In</SelectItem>
                    <SelectItem value="WARRANTY_WARRANTY_CLAIM">Warranty Claim</SelectItem>
                    <SelectItem value="WARRANTY_REPLACEMENT">Warranty Replacement</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Item name..."
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  data-testid="item-search"
                />
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date" />
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="end-date" />
                <Button onClick={fetchLogs} disabled={loading} data-testid="apply-filters">
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </div>

              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={suspiciousOnly}
                    onChange={(e) => setSuspiciousOnly(e.target.checked)}
                    data-testid="suspicious-toggle"
                  />
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="font-medium">Show suspicious only</span>
                  <span className="text-xs text-gray-500">(large recounts ≥10 or missing reason)</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{filteredLogs.length} record(s)</span>
                  <Button size="sm" variant="outline" onClick={exportLogsCSV} data-testid="export-csv">
                    <Download className="h-3.5 w-3.5 mr-1" />
                    Export CSV
                  </Button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm" data-testid="logs-table">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Timestamp</th>
                      <th className="px-3 py-2 text-left">Action</th>
                      <th className="px-3 py-2 text-left">Branch</th>
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-right">Qty Change</th>
                      <th className="px-3 py-2 text-right">Before → After</th>
                      <th className="px-3 py-2 text-left">Location</th>
                      <th className="px-3 py-2 text-left">Encoder</th>
                      <th className="px-3 py-2 text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading ? (
                      <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading...</td></tr>
                    ) : filteredLogs.length === 0 ? (
                      <tr><td colSpan={9} className="text-center py-8 text-gray-400">No movements found</td></tr>
                    ) : filteredLogs.map((log, idx) => {
                      const suspicious = isSuspicious(log);
                      const qty = Number(log.quantity || 0);
                      const color = ACTION_TYPE_COLORS[log.action_type] || "bg-gray-100 text-gray-800 border-gray-200";
                      return (
                        <tr key={log.id || idx} className={suspicious ? "bg-amber-50/60" : ""} data-testid={`log-row-${idx}`}>
                          <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{formatDateTime(log.timestamp)}</td>
                          <td className="px-3 py-2">
                            <Badge className={`${color} border text-[10px] font-medium`}>
                              {(log.action_type || "").replace(/_/g, " ")}
                            </Badge>
                            {suspicious && (
                              <AlertTriangle className="h-3.5 w-3.5 text-amber-600 inline ml-1" title="Suspicious activity" />
                            )}
                          </td>
                          <td className="px-3 py-2 font-medium">{log.branch || log.from_branch || "-"}</td>
                          <td className="px-3 py-2">{log.item_name || "-"}</td>
                          <td className={`px-3 py-2 text-right font-mono font-semibold ${qty < 0 ? "text-red-600" : qty > 0 ? "text-green-600" : "text-gray-500"}`}>
                            {qty > 0 ? "+" : ""}{qty}
                          </td>
                          <td className="px-3 py-2 text-right text-xs text-gray-600 whitespace-nowrap">
                            {log.previous_stock !== undefined && log.new_stock !== undefined
                              ? `${log.previous_stock} → ${log.new_stock}`
                              : "-"}
                          </td>
                          <td className="px-3 py-2 text-xs">{log.location || "-"}</td>
                          <td className="px-3 py-2 text-xs">{log.encoder || "-"}</td>
                          <td className="px-3 py-2 text-xs text-gray-500 max-w-xs truncate" title={log.notes}>{log.notes || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {tab === "tally" && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileWarning className="h-4 w-4 text-amber-600" />
                Daily Tally Report — Expected vs Actual Stock
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1">
                Formula: <strong>Expected = Received − Sold − TransferredOut − WarrantyClaimed</strong>.<br />
                Discrepancy means may stock na hindi naa-account (possible theft / wrong recording).
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Select value={tallyBranch} onValueChange={setTallyBranch}>
                  <SelectTrigger className="w-32" data-testid="tally-branch"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BRANCHES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={String(tallyDays)} onValueChange={(v) => setTallyDays(Number(v))}>
                  <SelectTrigger className="w-32" data-testid="tally-days"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="14">Last 14 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="60">Last 60 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={fetchTally} disabled={tallyLoading} data-testid="run-tally">
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${tallyLoading ? "animate-spin" : ""}`} />
                  Run Tally
                </Button>
                <span className="text-xs text-gray-500 ml-auto">{tally.length} items</span>
              </div>

              {/* Summary */}
              {tally.length > 0 && (() => {
                const discrepant = tally.filter((t) => t.discrepancy !== 0);
                const overage = discrepant.filter((t) => t.discrepancy > 0);
                const shortage = discrepant.filter((t) => t.discrepancy < 0);
                return (
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                      <div className="text-xs text-emerald-700 font-medium">Matched</div>
                      <div className="text-2xl font-bold text-emerald-700">{tally.length - discrepant.length}</div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded p-3">
                      <div className="text-xs text-amber-700 font-medium">Overage (sobra)</div>
                      <div className="text-2xl font-bold text-amber-700">{overage.length}</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                      <div className="text-xs text-red-700 font-medium">Shortage (kulang)</div>
                      <div className="text-2xl font-bold text-red-700">{shortage.length}</div>
                    </div>
                  </div>
                );
              })()}

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm" data-testid="tally-table">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-right">Opening</th>
                      <th className="px-3 py-2 text-right">+ Received</th>
                      <th className="px-3 py-2 text-right">− Sold</th>
                      <th className="px-3 py-2 text-right">− Transferred Out</th>
                      <th className="px-3 py-2 text-right">+ Transferred In</th>
                      <th className="px-3 py-2 text-right">− Warranty Out</th>
                      <th className="px-3 py-2 text-right">− Pull Out</th>
                      <th className="px-3 py-2 text-right font-semibold">Expected</th>
                      <th className="px-3 py-2 text-right font-semibold">Actual</th>
                      <th className="px-3 py-2 text-right font-semibold">Discrepancy</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {tallyLoading ? (
                      <tr><td colSpan={11} className="text-center py-8 text-gray-400">Loading...</td></tr>
                    ) : tally.length === 0 ? (
                      <tr><td colSpan={11} className="text-center py-8 text-gray-400">Click "Run Tally" to compute.</td></tr>
                    ) : tally.filter((t) => t.discrepancy !== 0 || t.actual > 0).slice(0, 500).map((t) => {
                      const d = t.discrepancy;
                      const rowColor = d === 0 ? "" : d > 0 ? "bg-amber-50/60" : "bg-red-50/60";
                      return (
                        <tr key={t.item_name} className={rowColor} data-testid={`tally-row-${t.item_name}`}>
                          <td className="px-3 py-2 font-medium">{t.item_name}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs">{t.opening || 0}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-green-700">+{t.received || 0}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-orange-700">−{t.sold || 0}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-purple-700">−{t.transferred_out || 0}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-purple-700">+{t.transferred_in || 0}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-amber-700">−{t.warranty_out || 0}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-red-700">−{t.pulled_out || 0}</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold">{t.expected}</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold">{t.actual}</td>
                          <td className={`px-3 py-2 text-right font-mono font-bold ${d === 0 ? "text-emerald-700" : d > 0 ? "text-amber-700" : "text-red-700"}`}>
                            {d > 0 ? "+" : ""}{d}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {tab === "alerts" && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                Discrepancy Alerts — Flagged Stock Losses
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1">
                Automatic flags: <strong>Transfer Variance</strong> (kulang ang natanggap sa transfer) at <strong>Unexplained Loss</strong> (nabawasan ang stock nang WALANG record).
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 mb-4">
                <Select value={alertStatusFilter} onValueChange={setAlertStatusFilter}>
                  <SelectTrigger className="w-36" data-testid="alert-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OPEN">🔴 Open</SelectItem>
                    <SelectItem value="RESOLVED">✅ Resolved</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={fetchAlerts} disabled={alertsLoading} size="sm" data-testid="refresh-alerts">
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${alertsLoading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
                <span className="text-xs text-gray-500 ml-auto">{alerts.length} alert(s)</span>
              </div>

              {alertsLoading ? (
                <div className="text-center py-8 text-gray-400">Loading...</div>
              ) : alerts.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <ShieldAlert className="h-12 w-12 mx-auto mb-2 opacity-40" />
                  <p>Walang {alertStatusFilter === "OPEN" ? "open na" : ""} discrepancy alerts. 🎉</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="alerts-list">
                  {alerts.map((a) => (
                    <div key={a.id} className={`p-3 border-2 rounded-lg ${a.status === "OPEN" ? "border-red-200 bg-red-50" : "border-gray-200 bg-gray-50"}`} data-testid={`alert-${a.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={a.type === "TRANSFER_VARIANCE" ? "bg-purple-100 text-purple-800" : "bg-red-100 text-red-800"}>
                              {a.type === "TRANSFER_VARIANCE" ? "🚚 Transfer Variance" : "❓ Unexplained Loss"}
                            </Badge>
                            <Badge className={a.status === "OPEN" ? "bg-red-600 text-white" : "bg-emerald-100 text-emerald-800"}>{a.status}</Badge>
                            <span className="font-semibold text-gray-900">{a.item_name}</span>
                          </div>
                          <div className="text-sm text-gray-700 mt-1">
                            {a.type === "TRANSFER_VARIANCE" ? (
                              <>Branch: <strong>{a.from_branch} → {a.to_branch}</strong> • Pinadala: <strong>{a.sent_qty}</strong> • Natanggap: <strong>{a.received_qty}</strong> • <span className="text-red-700 font-bold">Kulang: {a.variance_qty}</span></>
                            ) : (
                              <>Branch: <strong>{a.branch}</strong> • Expected: <strong>{a.expected_qty}</strong> • Actual: <strong>{a.actual_qty}</strong> • <span className={`font-bold ${a.variance_qty < 0 ? "text-red-700" : "text-amber-700"}`}>{a.variance_qty > 0 ? "Sobra" : "Nawawala"}: {Math.abs(a.variance_qty)}</span></>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {a.reason && <>Reason: {a.reason} • </>}
                            Reported by: {a.reported_by} • {formatDateTime(a.created_at)}
                            {a.status === "RESOLVED" && a.resolution && <div className="text-emerald-700 mt-0.5">✅ {a.resolution} — {a.resolved_by}</div>}
                          </div>
                        </div>
                        {a.status === "OPEN" && isAdmin() && (
                          <Button size="sm" variant="outline" onClick={() => { setResolvingAlert(a); setResolutionText(""); }} data-testid={`resolve-alert-${a.id}`}>
                            Resolve
                          </Button>
                        )}
                      </div>
                      {resolvingAlert?.id === a.id && (
                        <div className="mt-2 flex items-center gap-2">
                          <Input
                            placeholder="Explanation kung bakit resolved (min 5 chars)..."
                            value={resolutionText}
                            onChange={(e) => setResolutionText(e.target.value)}
                            className="h-8 text-xs"
                            data-testid="resolution-input"
                          />
                          <Button size="sm" onClick={resolveAlert} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-resolve">Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setResolvingAlert(null)}>Cancel</Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === "snapshot" && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                Snapshot Watch — Daily Stock Comparison
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1">
                Automatic na kinukunan ng "picture" ang stock araw-araw. Ikinukumpara ang snapshot vs current stock — kung may nabawas na <strong>walang record</strong> (sale/transfer/pull-out/recount), lalabas dito bilang <strong className="text-red-600">UNEXPLAINED</strong>.
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Select value={snapBranch} onValueChange={setSnapBranch}>
                  <SelectTrigger className="w-32" data-testid="snap-branch"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BRANCHES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button onClick={fetchSnapReport} disabled={snapLoading} data-testid="run-snapshot-report">
                  <RefreshCw className={`h-3.5 w-3.5 mr-1 ${snapLoading ? "animate-spin" : ""}`} />
                  Run Check
                </Button>
                {isAdmin() && (
                  <Button variant="outline" onClick={takeSnapshotNow} data-testid="take-snapshot-btn">
                    <Calendar className="h-3.5 w-3.5 mr-1" />
                    Take Snapshot Now
                  </Button>
                )}
                {snapReport?.snapshot && (
                  <span className="text-xs text-gray-500 ml-auto">
                    Snapshot: <strong>{snapReport.snapshot.date}</strong> ({snapReport.snapshot.taken_by}) • {snapReport.snapshot.total_units} units
                  </span>
                )}
              </div>

              {snapReport && !snapReport.snapshot ? (
                <div className="text-center py-8 text-gray-400">
                  <Calendar className="h-12 w-12 mx-auto mb-2 opacity-40" />
                  <p>{snapReport.message}</p>
                </div>
              ) : snapReport && (
                <>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                      <div className="text-xs text-emerald-700 font-medium">Items Checked</div>
                      <div className="text-2xl font-bold text-emerald-700">{snapReport.total_items_checked}</div>
                    </div>
                    <div className={`rounded p-3 border ${snapReport.unexplained_count > 0 ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
                      <div className={`text-xs font-medium ${snapReport.unexplained_count > 0 ? "text-red-700" : "text-emerald-700"}`}>⚠️ Unexplained Changes</div>
                      <div className={`text-2xl font-bold ${snapReport.unexplained_count > 0 ? "text-red-700" : "text-emerald-700"}`} data-testid="unexplained-count">{snapReport.unexplained_count}</div>
                    </div>
                  </div>

                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-sm" data-testid="snapshot-table">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-600">
                        <tr>
                          <th className="px-3 py-2 text-left">Item</th>
                          <th className="px-3 py-2 text-right">Snapshot</th>
                          <th className="px-3 py-2 text-right">+ In</th>
                          <th className="px-3 py-2 text-right">− Out</th>
                          <th className="px-3 py-2 text-right">± Recount</th>
                          <th className="px-3 py-2 text-right font-semibold">Expected</th>
                          <th className="px-3 py-2 text-right font-semibold">Actual</th>
                          <th className="px-3 py-2 text-right font-semibold">Unexplained</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {snapLoading ? (
                          <tr><td colSpan={8} className="text-center py-8 text-gray-400">Loading...</td></tr>
                        ) : (snapReport.items || []).length === 0 ? (
                          <tr><td colSpan={8} className="text-center py-8 text-gray-400">Walang changes since snapshot. All good! 🎉</td></tr>
                        ) : snapReport.items.map((t) => {
                          const u = t.unexplained;
                          return (
                            <tr key={t.item_name} className={u !== 0 ? "bg-red-50/70" : ""} data-testid={`snap-row-${t.item_name}`}>
                              <td className="px-3 py-2 font-medium">{t.item_name}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs">{t.snapshot_qty}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs text-green-700">+{(t.received || 0) + (t.transferred_in || 0)}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs text-orange-700">−{(t.sold || 0) + (t.transferred_out || 0) + (t.pulled_out || 0) + (t.warranty_out || 0)}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs text-blue-700">{t.recount_delta > 0 ? "+" : ""}{t.recount_delta || 0}</td>
                              <td className="px-3 py-2 text-right font-mono font-semibold">{t.expected}</td>
                              <td className="px-3 py-2 text-right font-mono font-semibold">{t.actual}</td>
                              <td className={`px-3 py-2 text-right font-mono font-bold ${u === 0 ? "text-emerald-700" : "text-red-700"}`}>
                                {u > 0 ? "+" : ""}{u} {u !== 0 && "⚠️"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
